/**
 * xterm.js terminal themes for the canvas terminal widget.
 *
 * xterm ships no named themes — these are standard community palettes expressed as
 * `ITheme` objects. The selected theme id is persisted on the node's Loro `data.theme`
 * and applied live via `term.options.theme`.
 */
import type { ITheme } from "@xterm/xterm";

export interface TerminalThemeOption {
  id: string;
  label: string;
  appearance: "dark" | "light";
  theme: ITheme;
}

export const TERMINAL_THEMES: TerminalThemeOption[] = [
  {
    id: "dark",
    label: "Dark",
    appearance: "dark",
    theme: {
      foreground: "#d4d4d4",
      background: "#1e1e1e",
      cursor: "#d4d4d4",
      cursorAccent: "#1e1e1e",
      selectionBackground: "#264f78",
      black: "#000000",
      red: "#cd3131",
      green: "#0dbc79",
      yellow: "#e5e510",
      blue: "#2472c8",
      magenta: "#bc3fbc",
      cyan: "#11a8cd",
      white: "#e5e5e5",
      brightBlack: "#666666",
      brightRed: "#f14c4c",
      brightGreen: "#23d18b",
      brightYellow: "#f5f543",
      brightBlue: "#3b8eea",
      brightMagenta: "#d670d6",
      brightCyan: "#29b8db",
      brightWhite: "#ffffff",
    },
  },
  {
    id: "light",
    label: "Light",
    appearance: "light",
    theme: {
      foreground: "#333333",
      background: "#ffffff",
      cursor: "#333333",
      cursorAccent: "#ffffff",
      selectionBackground: "#add6ff",
      black: "#000000",
      red: "#cd3131",
      green: "#00bc00",
      yellow: "#949800",
      blue: "#0451a5",
      magenta: "#bc05bc",
      cyan: "#0598bc",
      white: "#555555",
      brightBlack: "#666666",
      brightRed: "#cd3131",
      brightGreen: "#14ce14",
      brightYellow: "#b5ba00",
      brightBlue: "#0451a5",
      brightMagenta: "#bc05bc",
      brightCyan: "#0598bc",
      brightWhite: "#a5a5a5",
    },
  },
  {
    id: "dracula",
    label: "Dracula",
    appearance: "dark",
    theme: {
      foreground: "#f8f8f2",
      background: "#282a36",
      cursor: "#f8f8f2",
      cursorAccent: "#282a36",
      selectionBackground: "#44475a",
      black: "#21222c",
      red: "#ff5555",
      green: "#50fa7b",
      yellow: "#f1fa8c",
      blue: "#bd93f9",
      magenta: "#ff79c6",
      cyan: "#8be9fd",
      white: "#f8f8f2",
      brightBlack: "#6272a4",
      brightRed: "#ff6e6e",
      brightGreen: "#69ff94",
      brightYellow: "#ffffa5",
      brightBlue: "#d6acff",
      brightMagenta: "#ff92df",
      brightCyan: "#a4ffff",
      brightWhite: "#ffffff",
    },
  },
  {
    id: "solarized-dark",
    label: "Solarized Dark",
    appearance: "dark",
    theme: {
      foreground: "#839496",
      background: "#002b36",
      cursor: "#93a1a1",
      cursorAccent: "#002b36",
      selectionBackground: "#073642",
      black: "#073642",
      red: "#dc322f",
      green: "#859900",
      yellow: "#b58900",
      blue: "#268bd2",
      magenta: "#d33682",
      cyan: "#2aa198",
      white: "#eee8d5",
      brightBlack: "#002b36",
      brightRed: "#cb4b16",
      brightGreen: "#586e75",
      brightYellow: "#657b83",
      brightBlue: "#839496",
      brightMagenta: "#6c71c4",
      brightCyan: "#93a1a1",
      brightWhite: "#fdf6e3",
    },
  },
  {
    id: "github-light",
    label: "GitHub Light",
    appearance: "light",
    theme: {
      foreground: "#24292f",
      background: "#ffffff",
      cursor: "#24292f",
      cursorAccent: "#ffffff",
      selectionBackground: "#b6d7ff",
      black: "#24292e",
      red: "#d73a49",
      green: "#28a745",
      yellow: "#dbab09",
      blue: "#0366d6",
      magenta: "#5a32a3",
      cyan: "#0598bc",
      white: "#6a737d",
      brightBlack: "#959da5",
      brightRed: "#cb2431",
      brightGreen: "#22863a",
      brightYellow: "#b08800",
      brightBlue: "#005cc5",
      brightMagenta: "#5a32a3",
      brightCyan: "#3192aa",
      brightWhite: "#d1d5da",
    },
  },
];

export const DEFAULT_TERMINAL_THEME_ID = "dark";

export const TERMINAL_THEME_MAP = new Map<string, TerminalThemeOption>(
  TERMINAL_THEMES.map((t) => [t.id, t]),
);

export function getTerminalTheme(id: unknown): TerminalThemeOption {
  return (
    TERMINAL_THEME_MAP.get(String(id ?? DEFAULT_TERMINAL_THEME_ID)) ??
    TERMINAL_THEME_MAP.get(DEFAULT_TERMINAL_THEME_ID)!
  );
}
